import { useState } from 'react';
import {
  ChannelType,
  CreateNotificationChannelInput,
  NotificationChannel,
  UpdateNotificationChannelInput,
  useCreateNotificationChannelMutation,
  useUpdateNotificationChannelMutation,
} from '../../../gql/generated';
import { useResponsive } from '../../../hooks/useResponsive';
import { WizardLayout } from '../../ui/WizardLayout';
import { AddChannelDialogProvider } from './AddChannelDialogContext';
import { ChannelForm } from './ChannelForm';
import { ChannelFormData } from './context';
import { useAddChannelDialog } from './useAddChannelDialog';
import { logger } from '../../../utils/logger';

export interface AddChannelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (channelId: string) => void;
  /** When true, renders as a subdialog (no overlay, with back button) */
  isSubdialog?: boolean;
  /** Text for back button when in subdialog mode */
  backButtonText?: string;
  /** The channel to edit. If provided, the dialog will be in edit mode. */
  editingChannel?: NotificationChannel | null;
}

/**
 * Convert an existing NotificationChannel to ChannelFormData for editing
 */
function convertChannelToFormData(
  channel: NotificationChannel
): Partial<ChannelFormData> {
  return {
    name: channel.name,
    channelType: channel.channelType,
    isActive: channel.isEnabled,
    email: channel.email ?? undefined,
    teamsWebhookUrl: channel.webhookUrl ?? undefined,
    groupedChannelIds: channel.groupedChannelIds ?? undefined,
  };
}

export function AddChannelDialog({
  open,
  onOpenChange,
  onSuccess,
  isSubdialog = false,
  backButtonText = 'Back',
  editingChannel,
}: AddChannelDialogProps) {
  // Convert editing channel to initial form data
  const initialFormData = editingChannel
    ? convertChannelToFormData(editingChannel)
    : undefined;

  return (
    <AddChannelDialogProvider
      key={editingChannel?.id ?? 'new'}
      initialFormData={initialFormData}
    >
      <AddChannelDialogContent
        open={open}
        onOpenChange={onOpenChange}
        onSuccess={onSuccess}
        isSubdialog={isSubdialog}
        backButtonText={backButtonText}
        editingChannel={editingChannel}
      />
    </AddChannelDialogProvider>
  );
}

function AddChannelDialogContent({
  open,
  onOpenChange,
  onSuccess,
  isSubdialog = false,
  backButtonText = 'Back',
  editingChannel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (channelId: string) => void;
  isSubdialog?: boolean;
  backButtonText?: string;
  editingChannel?: NotificationChannel | null;
}) {
  const { formData, setFormData } = useAddChannelDialog();
  const { prefersMobileLayout } = useResponsive();
  const [createChannel, { loading: createLoading }] =
    useCreateNotificationChannelMutation();
  const [updateChannel, { loading: updateLoading }] =
    useUpdateNotificationChannelMutation();
  const [error, setError] = useState<string | null>(null);

  const isEditMode = !!editingChannel;
  const loading = createLoading || updateLoading;

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
    if (!newOpen) {
      // Reset form after animation completes
      setTimeout(() => {
        setFormData({
          name: '',
          channelType: ChannelType.Email,
          isActive: true,
          email: '',
          teamsWebhookUrl: '',
          groupedChannelIds: [],
        });
        setError(null);
      }, 300);
    }
  };

  const handleClose = () => {
    handleOpenChange(false);
  };

  const validateForm = (): string | null => {
    if (!formData.name.trim()) {
      return 'Channel name is required';
    }

    if (formData.channelType === ChannelType.Email) {
      if (!formData.email?.trim()) {
        return 'Email recipients are required';
      }
      // Basic email validation
      const emails = formData.email
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean);
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = emails.filter((email) => !emailRegex.test(email));
      if (invalidEmails.length > 0) {
        return `Invalid email address(es): ${invalidEmails.join(', ')}`;
      }
    }

    if (formData.channelType === ChannelType.Teams) {
      if (!formData.teamsWebhookUrl?.trim()) {
        return 'Teams webhook URL is required';
      }
      // Basic URL validation
      try {
        new URL(formData.teamsWebhookUrl);
      } catch {
        return 'Invalid webhook URL';
      }
    }

    if (formData.channelType === ChannelType.Grouped) {
      if (
        !formData.groupedChannelIds ||
        formData.groupedChannelIds.length === 0
      ) {
        // For now, we'll allow empty grouped channels
        // In a full implementation, you'd want to select channels
      }
    }

    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setError(null);

      if (isEditMode && editingChannel) {
        // Update existing channel
        const updateInput: UpdateNotificationChannelInput = {
          name: formData.name,
          channelType: formData.channelType,
          isEnabled: formData.isActive,
        };

        // Add email field for Email channels
        if (formData.channelType === ChannelType.Email && formData.email) {
          updateInput.email = formData.email;
        }

        // Add webhookUrl for Teams channels
        if (
          formData.channelType === ChannelType.Teams &&
          formData.teamsWebhookUrl
        ) {
          updateInput.webhookUrl = formData.teamsWebhookUrl;
        }

        // Add groupedChannelIds for Grouped channels
        if (
          formData.channelType === ChannelType.Grouped &&
          formData.groupedChannelIds &&
          formData.groupedChannelIds.length > 0
        ) {
          updateInput.groupedChannelIds = formData.groupedChannelIds;
        }

        const result = await updateChannel({
          variables: { id: editingChannel.id, input: updateInput },
          refetchQueries: ['GetNotificationChannels'],
        });

        const updatedChannelId = result.data?.updateNotificationChannel.id;
        if (updatedChannelId) {
          onSuccess?.(updatedChannelId);
        }
      } else {
        // Create new channel
        const createInput: CreateNotificationChannelInput = {
          name: formData.name,
          channelType: formData.channelType,
          isActive: formData.isActive,
        };

        // Add email field for Email channels
        if (formData.channelType === ChannelType.Email && formData.email) {
          createInput.email = formData.email;
        }

        // Add webhookUrl for Teams channels
        if (
          formData.channelType === ChannelType.Teams &&
          formData.teamsWebhookUrl
        ) {
          createInput.webhookUrl = formData.teamsWebhookUrl;
        }

        // Add groupedChannelIds for Grouped channels
        if (
          formData.channelType === ChannelType.Grouped &&
          formData.groupedChannelIds &&
          formData.groupedChannelIds.length > 0
        ) {
          createInput.groupedChannelIds = formData.groupedChannelIds;
        }

        const result = await createChannel({
          variables: { input: createInput },
          refetchQueries: ['GetNotificationChannels'],
        });

        const createdChannelId = result.data?.createNotificationChannel.id;
        if (createdChannelId) {
          onSuccess?.(createdChannelId);
        }
      }

      handleClose();
    } catch (err) {
      logger.error(
        isEditMode ? 'Failed to update channel:' : 'Failed to create channel:',
        err
      );
      setError(
        err instanceof Error
          ? err.message
          : isEditMode
            ? 'Failed to update channel'
            : 'Failed to create channel'
      );
    }
  };

  if (!open) {
    return null;
  }

  const wizardTitle = isSubdialog
    ? isEditMode
      ? 'Edit channel'
      : 'New channel'
    : isEditMode
      ? 'Edit Channel'
      : 'Add Channel';

  const completeLabel = loading
    ? isEditMode
      ? 'Saving...'
      : 'Creating...'
    : isEditMode
      ? 'Save changes'
      : isSubdialog
        ? 'Create channel'
        : 'Create new channel';

  return (
    <section
      className='fixed z-70 flex flex-col'
      style={
        prefersMobileLayout
          ? {
              top: 'var(--spacing-header-height-mobile)',
              left: 0,
              right: 0,
              bottom: 0,
            }
          : {
              top: 'var(--spacing-header-height)',
              left: 'var(--spacing-sidebar-width)',
              right: 0,
              bottom: 0,
            }
      }
    >
      <WizardLayout
        title={wizardTitle}
        stepLabels={['Channel details']}
        currentStep={1}
        completeLabel={completeLabel}
        nextDisabled={loading}
        error={error}
        onClose={handleClose}
        onComplete={handleSubmit}
        cancelLabel={isSubdialog ? backButtonText : 'Cancel'}
      >
        <div className='flex h-full w-full items-start justify-center overflow-y-auto p-6 md:p-8'>
          <div
            className='bg-surface-primary border-border-primary flex w-full
              max-w-132 flex-col gap-6 rounded-xl border p-6
              shadow-[0px_8px_20px_0px_rgba(0,0,0,0.08)] md:p-8'
          >
            <ChannelForm />
          </div>
        </div>
      </WizardLayout>
    </section>
  );
}
